// Per-project case-study data consumed by containers/projectDetail/ProjectDetail.js.
// Each project lives at /projects/:slug. Screenshots can be local imports OR full URLs.

import acharyamulLogo from "../assets/images/acharyamul.svg";
import eventrixLogo from "../assets/images/eventrix.svg";
import jaimeeLogo from "../assets/images/jaimee.jpeg";

// Eventrix screenshots (copied from codeaskush.github.io/screenshots)
import eventrixShot0 from "../assets/images/eventrix/e0.jpg";
import eventrixShot1 from "../assets/images/eventrix/e1.jpg";
import eventrixShot2 from "../assets/images/eventrix/e2.jpg";
import eventrixShot3 from "../assets/images/eventrix/e3.jpg";
import eventrixShot4 from "../assets/images/eventrix/e4.jpg";
import eventrixShot5 from "../assets/images/eventrix/e5.jpg";
import eventrixShot6 from "../assets/images/eventrix/e6.jpg";

const projects = {
  jaimee: {
    slug: "jaimee",
    name: "Jaimee: Long-Term-Memory POC",
    tagline:
      "A Neo4j-backed long-term-memory layer for an AI companion, built as my UniSC SCI700 capstone.",
    logo: jaimeeLogo,
    period: "Trimester 1, 2026",
    status: "Capstone in progress",
    role: "Sole engineer (capstone) · Backend / AI Intern at Jaimee.ai",
    links: [
      {label: "Jaimee.ai", url: "https://jaimee.ai"}
    ],
    // Demo video — host on Google Drive (share: "Anyone with the link"),
    // then paste the FULL share URL or just the file ID here. The ProjectDetail
    // component handles both formats automatically.
    video: {
      url: "https://drive.google.com/file/d/1sNHLpKUmhLWFGl5UAaZZ8G2rRtFCacUl/view?usp=sharing",
      caption: "Jaimee long-term-memory POC walkthrough"
    },
    techStack: [
      "Python 3.11",
      "FastAPI",
      "Celery + Redis",
      "Neo4j 5 + Graph Data Science",
      "PostgreSQL 16",
      "pgvector",
      "LangChain",
      "OpenAI",
      "Anthropic",
      "Zep",
      "Vite + React (dashboard)"
    ],
    sections: [
      {
        heading: "The problem",
        body:
          "Conversational AI systems forget. Production LLM apps either re-prompt the entire history (expensive, lossy past a few thousand tokens) or stuff a flat vector store (no relational reasoning, no temporal awareness). Jaimee's product hypothesis was that a knowledge-graph-backed memory layer could give an AI companion both episodic recall and structured cross-conversation reasoning at a fraction of the token cost."
      },
      {
        heading: "What I built",
        body:
          "A long-term-memory subsystem behind a small dashboard. FastAPI serves the read/write endpoints; Celery + Redis handle async embedding and graph-update jobs; Neo4j stores entities and relationships shaped by the Zep ontology; pgvector backs the retrieval layer; LangChain orchestrates the RAG pipeline; OpenAI and Anthropic LLMs are routed through a hybrid policy. Planned through a full product brief, PRD with 54 functional and 31 non-functional requirements, architecture decision record, and 8 epics / 60 stories before a single line of implementation code was written."
      },
      {
        heading: "Architecture decisions worth noting",
        body:
          "Splitting infrastructure (DBs in Docker) from application code (host process during dev) cut local iteration time substantially: hot reload + native debugger access mattered more than perfect Docker parity until the Week 12 reviewer-replication test. Choosing Neo4j over a pure vector store was the bet, because graphs let retrieval queries reason over relationships, not just nearest-neighbour distance. The trade-off is operational complexity, which the eval framework is there to validate."
      },
      {
        heading: "What I learned",
        body:
          "Specs prevent rework, not creativity. Writing 60 stories before code felt slow until the first sprint, when I realised I'd resolved most of the ambiguity already and could just build. Second lesson: knowledge graphs are humbling. Designing an ontology that's both expressive and queryable means thinking like a librarian, not just an engineer, and getting it wrong costs you in Cypher complexity downstream. Third: integrating two LLM providers behind one interface taught me where the abstractions leak (token counting, retry semantics, streaming) and how to pick boundaries."
      }
    ],
    screenshots: []
  },

  acharyamul: {
    slug: "acharyamul",
    name: "Acharyamul (Rootslegx)",
    tagline:
      "A collaborative bilingual family-tree platform for the Acharya family of Tanahun, Nepal.",
    logo: acharyamulLogo,
    period: "2025 – 2026",
    status: "Live · acharyamul.kushagra.com.np",
    role: "Solo full-stack engineer · Product designer · DevOps",
    links: [
      {label: "Visit Live Site", url: "https://acharyamul.kushagra.com.np"}
    ],
    techStack: [
      "React 19 + Vite",
      "Tailwind CSS + shadcn/ui",
      "FastAPI",
      "SQLAlchemy 2.0",
      "PostgreSQL 16",
      "React Flow + D3.js",
      "Google OAuth 2.0",
      "JWT (httpOnly cookies)",
      "Docker Compose",
      "Nginx"
    ],
    sections: [
      {
        heading: "The problem",
        body:
          "The Acharya family of Tanahun, Nepal had no canonical record of its members, marriages, or lineage. What existed was scattered across hand-written notebooks, oral history and a few spreadsheets. A static printout wasn't enough: families grow, names get added, mistakes need fixing, and the people who know the corrections rarely have access to the editor."
      },
      {
        heading: "What I built",
        body:
          "A bilingual (English / Devanagari) web app where the family chart is the homepage. Vertical generational view via React Flow, horizontal D3.js view as an alternate. Per-person profiles with employment, life events, and a live event timeline. Contributors can suggest corrections or new people via a moderated workflow; admins approve or reject from a single panel. A relationship calculator surfaces Nepali kinship terms by walking the graph. Daily automated backups; full Docker Compose deployment behind Nginx."
      },
      {
        heading: "Architecture decisions worth noting",
        body:
          "Splitting the chart into two independent renderers (React Flow vertical, D3 horizontal) instead of one configurable component traded ~30% extra code for two clean mental models: each layout owns its own coordinate maths, no flag-driven branching. Contribution workflow uses pending/quarantine tables in PostgreSQL rather than a separate review service; cheaper, and admin review fits inside the same Postgres transactions as the underlying graph edits."
      },
      {
        heading: "What I learned",
        body:
          "Designing for non-technical contributors is harder than designing for engineers. Every error message, validation prompt and confirmation modal had to work for a 70-year-old grandparent inputting Devanagari names on a phone. Bilingual data modelling, storing both Roman and Devanagari for every name field with consistent transliteration, was the silent feature that made or broke usability. Finally: shipping a real product for real users (who'll send you a text saying 'this is wrong, fix it') is the only feedback loop that teaches you what you actually built."
      }
    ],
    screenshots: []
  },

  eventrix: {
    slug: "eventrix",
    name: "Eventrix",
    tagline:
      "End-to-end event-management SaaS. Ticketing, venue ops, terminals, CRM, reporting.",
    logo: eventrixLogo,
    period: "2025 – 2026",
    status: "Operational · demo-operator.eventrix.cc",
    role: "Solo founder · Full-stack engineer · UX designer",
    links: [
      {label: "Live Demo", url: "https://demo-operator.eventrix.cc"}
    ],
    techStack: [
      "React + TypeScript",
      "NestJS",
      "PostgreSQL",
      "Prisma",
      "Role-based access control",
      "Multi-tenant isolation",
      "Stripe (ticketing)",
      "Docker",
      "Nginx"
    ],
    sections: [
      {
        heading: "The problem",
        body:
          "Event operators in mid-market venues juggle 3 to 5 disconnected tools: one for ticketing, one for seat maps, one for terminal/device control on the day, one for CRM, and a spreadsheet for the reporting their stakeholders actually want. Switching costs are high and the data never reconciles. The opportunity was a single product that holds the whole event lifecycle in one tenant."
      },
      {
        heading: "What I built",
        body:
          "A multi-tenant SaaS covering: digital ticketing with Stripe checkout; visual venue and seat-map editor; terminal/device control panel for on-the-day operations; marketing CRM with segmentation and broadcast; real-time attendance reporting dashboards. Role-based access (operator / staff / scanner / read-only) enforced at the API layer. Designed the UI from blank Figma boards; owned API design, deployment, and the operator demo environment."
      },
      {
        heading: "Architecture decisions worth noting",
        body:
          "Multi-tenant isolation via tenant_id row-level constraints, not separate databases. Cheaper to operate, and PostgreSQL's RLS made it safe. Real-time reporting uses materialised views refreshed on a Celery schedule rather than streaming queries; for the audience sizes involved (single-venue events of <50k attendees), 30-second freshness was indistinguishable from real-time at a fraction of the engineering cost. The seat-map editor stores layouts as JSON polygons rather than rendering to canvas; trade-off was simpler version control and admin-friendly diffs."
      },
      {
        heading: "What I learned",
        body:
          "Scope is a product decision, not an engineering one. The first version of Eventrix tried to do too much in too few weeks; I cut the CRM module twice before keeping it, and it's the module operators most often demo to stakeholders. Second: designing a multi-tenant SaaS as a solo founder forced me to make defensive choices early (tenant_id everywhere, audit logs on day one, no shared state across requests) that paid off as soon as the first real customer touched it. Third: a working demo environment isn't a nice-to-have, it's the sales motion."
      }
    ],
    screenshots: [
      {src: eventrixShot0, caption: "Operator dashboard, live event overview"},
      {src: eventrixShot1, caption: "Ticketing & seat-map editor"},
      {src: eventrixShot2, caption: "Venue management and device control"},
      {src: eventrixShot3, caption: "Marketing CRM segmentation"},
      {src: eventrixShot4, caption: "Real-time attendance reporting"},
      {src: eventrixShot5, caption: "Terminal-side scanner UI"},
      {src: eventrixShot6, caption: "Operator settings and access control"}
    ]
  }
};

export default projects;
